import type { CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, WifiOff } from 'lucide-react';
import { supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useAllowedGameGroups, useGameGroupsStore } from '@/stores/gameGroups.store';
import { gameGroupHeaderFor, memberGroups, usesGameGroup } from '@/utils/gameGroups';
import type { ProjectGroupDTO } from '@/services/api.service';
import styles from './GameGroupSwitcher.module.scss';

/** Couleur d'un groupe sans couleur au CICD : ton neutre, comme le GroupBadge du Déploiement. */
const NEUTRAL = 'var(--color-text-secondary)';

function optionTitle(g: ProjectGroupDTO): string {
  const parts: string[] = [];
  if (!g.available) {
    parts.push(g.reason ?? `${g.label} indisponible.`);
  } else if (!g.healthy) {
    parts.push(`${g.label} : satellite injoignable à la dernière sonde`
      + (g.healthError ? ` (${g.healthError})` : '')
      + ". Il reste choisissable : ses pages diront s'il répond.");
  } else {
    parts.push(`Groupe ${g.label}${g.root ? ' (satellite racine)' : ''}`);
  }
  if (g.warnings?.length) parts.push(...g.warnings);
  return parts.join('\n');
}

/**
 * Bascule « Nova | Iris » à droite du sélecteur de jeu : choisit le groupe de jeu dont les pages
 * lisent les données (satellite du groupe). Visible seulement pour un jeu à groupes (NationsGlory)
 * et sur une page qui dépend du groupe (usesGameGroup) : ni Paladium, ni les pages servies par le
 * panel, sauf Grades et accès, dont la liste des membres suit le groupe choisi.
 *
 * - Groupe hors de l'affectation du membre du staff (`not-member`) : jamais affiché.
 * - Groupe indisponible (pas de satellite, retiré du CICD) : option désactivée, motif en infobulle.
 * - Groupe injoignable à la dernière sonde : option ACTIVE avec une pastille ; la santé ne désactive
 *   jamais un groupe (une coupure d'une minute ne doit pas renvoyer un modérateur sur Nova).
 * - Changer de groupe remonte les pages (clé de l'Outlet dans AdminLayout) : elles relisent les
 *   données du nouveau groupe sans rechargement complet.
 *
 * @param emphasis mise en avant (Supervision, Statistiques) : liseré coloré
 */
export function GameGroupSwitcher({ emphasis = false }: { emphasis?: boolean }) {
  const { pathname } = useLocation();
  const gameId = useGameStore((s) => s.activeGameId);
  const known = useGameStore((s) => !!s.gameGroupsKnown[s.activeGameId]);
  const current = useGameStore((s) => gameGroupHeaderFor(s.activeGameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  const setActiveGameGroup = useGameStore((s) => s.setActiveGameGroup);
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const status = useGameGroupsStore((s) => s.status);
  const error = useGameGroupsStore((s) => s.error);
  const allowed = useAllowedGameGroups(gameId);

  if (!supportsGameGroups(gameId) || !usesGameGroup(pathname)) return null;

  if (!entry) {
    // Lecture des groupes en échec : le groupe persisté part quand même (la passerelle reste juge).
    // Badge figé plutôt que rien, sinon le modérateur ne saurait pas quel groupe il consulte.
    if (status === 'error' && known && current) {
      return (
        <span
          className={styles.degraded}
          title={`Groupes de jeu indisponibles${error ? ` (${error})` : ''}. Les pages lisent « ${current} », `
            + "le groupe choisi la dernière fois ; la passerelle refuse un groupe qui n'existe plus."}
        >
          <AlertTriangle size={13} />
          <span className={styles.label}>{current}</span>
        </span>
      );
    }
    return null;
  }

  // Seuls les groupes de l'affectation sont proposés ; aucun : le layout l'explique à la place de la page.
  // Un groupe sans satellite déclaré (indisponible et sans aucune capacité annoncée, ex. un groupe
  // d'infrastructure comme « lobby ») n'a rien à superviser : il n'alourdit pas le sélecteur.
  const groups = memberGroups(entry, allowed).filter((g) => g.available || g.capabilities.length > 0);
  if (groups.length === 0) return null;
  const active = groups.find((g) => g.key === current);
  const activeColor = active?.color ?? NEUTRAL;

  // Un seul groupe connu : badge statique, sans bascule (comme le sélecteur de jeu à un projet).
  if (groups.length === 1) {
    const only = groups[0]!;
    return (
      <span
        className={`${styles.single} ${emphasis ? styles.emphasis : ''}`}
        style={{ '--group-color': only.color ?? NEUTRAL } as CSSProperties}
        title={optionTitle(only)}
      >
        <span className={styles.dot} />
        <span className={styles.label}>{only.label}</span>
        {only.available && !only.healthy && <WifiOff size={12} className={styles.down} aria-label="injoignable" />}
      </span>
    );
  }

  return (
    <div
      className={`${styles.switcher} ${emphasis ? styles.emphasis : ''}`}
      style={{ '--group-color': activeColor } as CSSProperties}
      role="radiogroup"
      aria-label="Groupe de jeu"
    >
      {groups.map((g) => {
        const isActive = g.key === current;
        return (
          <button
            key={g.key}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={!g.available}
            title={optionTitle(g)}
            aria-label={`${g.label}${g.available && !g.healthy ? ' (injoignable)' : ''}`}
            className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
            style={{ '--option-color': g.color ?? NEUTRAL } as CSSProperties}
            onClick={() => { if (!isActive) setActiveGameGroup(gameId, g.key); }}
          >
            <span className={styles.dot} />
            <span className={styles.label}>{g.label}</span>
            {g.available && !g.healthy && <WifiOff size={12} className={styles.down} aria-label="injoignable" />}
            {(g.warnings?.length ?? 0) > 0 && <AlertTriangle size={12} className={styles.warn} aria-label="avertissement" />}
          </button>
        );
      })}
    </div>
  );
}
